import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsQuery_ActivityFragment
} from "@outschool/gql-frontend-generated";
import { selfPacedEnrollPath } from "@outschool/routes";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import { ClassScheduleRequestModal } from "../../ClassScheduleRequest";
import useCDPEnrollOrSeeScheduleCTA from "../hooks/useCDPEnrollOrSeeScheduleCTA";
import useCDPMembershipModal from "../hooks/useCDPMembershipModal";
import useEnrollmentUrl from "../hooks/useEnrollmentUrl";
import ActivityEnrollableTimesCta from "./ActivityEnrollableTimesCta";
import { SectionCallToAction } from "./SectionCallToActionButtons";

interface MobileSeeScheduleButtonProps {
  activity: ClassDetailsQuery_ActivityFragment;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  setIsAvailabilityModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MobileSeeScheduleButton({
  activity,
  activityWithEnrollableTimes,
  setIsAvailabilityModalOpen
}: MobileSeeScheduleButtonProps) {
  const { currentUser, userTimeZone, isLoggedIn } = useSession();
  const navigate = useNavigation();
  const location = useLocation();
  const { enterLoginFlow } = useLoginFlowContext();
  const { shouldShowCDPMembershipModal } = useCDPMembershipModal();
  const ctaText = useCDPEnrollOrSeeScheduleCTA({ activity });

  const [openClassScheduleRequest, setOpenClassScheduleRequest] =
    useState(false);

  const generateEnrollmentUrl = useEnrollmentUrl();

  const onClickViewAvailability = useCallback(() => {
    if (activity.isSelfPaced) {
      const path = selfPacedEnrollPath(activity);
      if (!isLoggedIn) {
        enterLoginFlow({
          authTrigger: AuthTrigger.ENROLL,
          ...generateEnrollmentUrl({ path, activity })
        });
      } else if (shouldShowCDPMembershipModal) {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set("joinMembershipModal", "true");
        queryParams.set("redirectAfterPath", path);
        navigate(`${location.pathname}?${queryParams.toString()}`, {
          replace: true
        });
      } else {
        navigate(path);
      }
    } else {
      setIsAvailabilityModalOpen(true);
    }
  }, [
    activity,
    enterLoginFlow,
    generateEnrollmentUrl,
    isLoggedIn,
    navigate,
    shouldShowCDPMembershipModal,
    location.search,
    location.pathname,
    setIsAvailabilityModalOpen
  ]);

  const onClickSeeSchedule = useCallback(() => {
    if (activity.isSelfPaced) {
      const path = selfPacedEnrollPath(activity);
      if (!isLoggedIn) {
        enterLoginFlow({
          authTrigger: AuthTrigger.ENROLL,
          ...generateEnrollmentUrl({ path, activity })
        });
      } else if (shouldShowCDPMembershipModal) {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set("joinMembershipModal", "true");
        queryParams.set("redirectAfterPath", path);
        navigate(`${location.pathname}?${queryParams.toString()}`, {
          replace: true
        });
      } else {
        navigate(path);
      }
    } else {
      setIsAvailabilityModalOpen(true);
    }
  }, [
    activity,
    enterLoginFlow,
    generateEnrollmentUrl,
    isLoggedIn,
    navigate,
    shouldShowCDPMembershipModal,
    location.search,
    location.pathname,
    setIsAvailabilityModalOpen
  ]);

  if (activity.isClub && activity.canEnroll) {
    return (
      <SectionCallToAction
        activity={activity}
        section={activity.clubSection}
        trackingSharedProperties={{
          component: "MobileStickyFooter"
        }}
      />
    );
  }

  return activity.hasTeacherSchedule ? (
    <>
      <ClassScheduleRequestModal
        activity={activity}
        user={currentUser}
        userTimeZone={userTimeZone!}
        open={openClassScheduleRequest}
        setOpen={setOpenClassScheduleRequest}
      />
      {!!activityWithEnrollableTimes && (
        <ActivityEnrollableTimesCta
          activityWithEnrollableTimes={activityWithEnrollableTimes}
          onRequestAnotherTimeClick={() => setOpenClassScheduleRequest(true)}
          onClickViewAvailability={onClickViewAvailability}
          shouldHideAlert
          buttonSize="small"
        />
      )}
    </>
  ) : (
    <>
      <TrackedButton
        variant="contained"
        size={"small"}
        trackingName={"see_schedule_mobile_nav"}
        trackingUniqueId={activity.uid}
        onClick={onClickSeeSchedule}
        data-test-id="see-schedule-button"
      >
        {ctaText}
      </TrackedButton>
    </>
  );
}
